/* eslint-disable @angular-eslint/component-class-suffix */
/* eslint-disable @angular-eslint/component-selector */
import {
  Component,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ViewChildren,
  QueryList,
  OnInit,
} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { DialogService } from '@migo-apps/dialogs';
import { Credentials } from '../../models/credentials.interface';
import { AuthenticationService } from '../../services/authentication.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ChangeDetectorRef } from '@angular/core';
import { finalize, first } from 'rxjs';
import { UserStatus } from '@shared/models';
import { TranslateService } from '@ngx-translate/core';
import { InputComponent } from '@migo-apps/migo-uikit-v4';
import { ToastService } from '@migo-apps/components';
import { BreakpointService } from '../../../shared/services/breakpoint/breakpoint.service';

const NO_CREDENTIALS_TITLE = 'AUTH.DIALOG.NO-CREDENTIALS.TITLE';
const NO_CREDENTIALS_MESSAGE = 'AUTH.DIALOG.NO-CREDENTIALS.MESSAGE';
const INVALID_CREDENTIALS_TITLE = 'AUTH.DIALOG.INVALID-CREDENTIALS.TITLE';
const INVALID_CREDENTIALS = 'AUTH.DIALOG.INVALID-CREDENTIALS.INVALID';
const USER_BLOCKED = 'AUTH.DIALOG.INVALID-CREDENTIALS.USER-BLOCKED';

/**
 * Pagina de inicio de sesion
 *
 * @export
 * @class LoginPage
 */
@Component({
  standalone: false,
  selector: 'login-page',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class LoginPage implements OnInit {
  @ViewChildren(InputComponent) inputs!: QueryList<InputComponent>;
  loginForm!: FormGroup;
  isRedirect = false;

  /**
   *Indica si un proceso asincrono esta en ejecucion,
   *Utilizado para mostrar al usuario una animacion de carga
   * @memberof LoginPage
   */
  loading = false;

  constructor(
    private builder: FormBuilder,
    private dialogSvc: DialogService,
    private authSvc: AuthenticationService,
    private router: Router,
    private cd: ChangeDetectorRef,
    private i18n: TranslateService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    public bp: BreakpointService
  ) {
    this.createForm();
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(first()).subscribe((params) => {
      this.isRedirect = params['isRedirect'] === 'true';
    });
  }

  /**
   * Metodo principal, envia la solicitud de inicio de sesion
   *
   * @memberof LoginPage
   */
  onSubmit() {
    if (this.loginForm.invalid) {
      this.i18n
        .get([NO_CREDENTIALS_TITLE, NO_CREDENTIALS_MESSAGE])
        .subscribe((value) => {
          this.dialogSvc.openInformationDialog({
            title: value[NO_CREDENTIALS_TITLE],
            message: value[NO_CREDENTIALS_MESSAGE],
          });
        });
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const credentials: Credentials = this.loginForm.value;
    this.authSvc
      .login(credentials, this.isRedirect)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cd.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.router.navigate(['dashboard'], {
            queryParams: { welcome: true },
          });
        },
        error: ({ error }) => {
          console.log('error', error);
          this.i18n
            .get([INVALID_CREDENTIALS, USER_BLOCKED, INVALID_CREDENTIALS_TITLE])
            .subscribe((value) => {
              const isBlocked = error.status === UserStatus.BLOCKED;
              const message = isBlocked
                ? value[USER_BLOCKED]
                : value[INVALID_CREDENTIALS];

              this.toastService.toast(message, 'error');
              const emailControl = this.loginForm.get('email');
              if (emailControl) {
                emailControl.setErrors({
                  manualError: error.status
                    ? 'Usuario bloqueado'
                    : 'Credenciales incorrectas',
                });
              }
              const passwordControl = this.loginForm.get('password');
              if (passwordControl) {
                passwordControl.setErrors({
                  manualError: error.status
                    ? 'Usuario bloqueado'
                    : 'Credenciales incorrectas',
                });
              }
            });
        },
      });
  }

  /**
   * We're creating a form group with three form controls: email, password, and
   * remember.
   *
   * The email form control has two validators: required and email.
   *
   * The password form control has one validator: required.
   *
   * The remember form control has one validator: required.
   *
   * The remember form control is a checkbox, so we're setting the default value to
   * false.
   */
  private createForm() {
    this.loginForm = this.builder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      remember: [false, Validators.required],
    });

    this.getRememberedEmail();
  }

  /**
   * If the user has a remembered email, set the email and remember form controls to
   * that email and true, respectively
   */
  private getRememberedEmail() {
    const email: string = this.authSvc.getRememberedEmail();

    if (email) {
      this.email?.setValue(email);
      this.remember?.setValue(true);
    }
  }

  onEnterKey(event: Event): void {
    event.preventDefault();
    const targetInput = event.target as HTMLInputElement;
    if (targetInput.name === 'emailInput') {
      const inputToFocus = this.inputs.find(
        (input) => input.id === 'passwordInput'
      );
      if (inputToFocus) {
        inputToFocus.setFocus();
      }
    } else if (targetInput.name === 'passwordInput') this.onSubmit();
  }

  get email() {
    return this.loginForm.get('email');
  }
  get password() {
    return this.loginForm.get('password');
  }
  get remember() {
    return this.loginForm.get('remember');
  }

  setRemember(enable: boolean) {
    this.loginForm.get('remember')?.setValue(enable);
  }
}
